import { __awaiter } from '../node_modules/tslib/tslib.es6.js';
import React__default, { useState, useEffect } from 'react';
import '../analytics/events.js';
import 'uuid';
import 'lodash/includes';
import { getAppConsistentParams } from '../utils/appUtils.js';
import { getEntityImage } from '../utils/assetUtils.js';
import '../utils/brandclubStoreConfigUtils.js';
import 'numeral';
import { decodeHTMLCharacters, formatMoney } from '../utils/stringUtils.js';
import '../utils/broadcast.js';
import '../utils/constants/Constants.js';
import 'awesome-phonenumber';
import 'lodash/isEqual';
import 'lodash/get';
import 'lodash/orderBy';
import '@splitsoftware/splitio-react';
import { CampaignType } from '../types/Campaign.js';
import { getPurchaseRewardAmountForProduct, getProductUrl, isServerSideCheckoutEligible } from '../utils/productUtils.js';
import '../api/graphQl/brand.js';
import '../api/graphQl/product.js';
import '../api/cache/appConfigQueryOptions.js';
import '@brandclub/types';
import 'lodash/omitBy';
import 'lodash/isNil';
import '../utils/logger.js';
import { FallbackImage } from '../Components/FallbackImage.js';
import { ConciergeRetailerIconGroup } from './components/ConciergeRetailerIconGroup.js';
import { ConciergeRewardPillWithText } from './components/ConciergeRewardPillWithText.js';
import { Button } from '../Components/ui/button.js';
import { cn } from '../lib/utils.js';
import _uniq from 'lodash/uniq';
import { CheckCircleIcon, ShoppingBag } from 'lucide-react';

const ConciergeProductCard = ({ product, onShareClick, onAddToCart, onOpenCart, isInCart: isInCartFn, showCartActions = true, }) => {
    const { affiliateRewardAmount, hasActiveInstantReward, matchedProducts, checkoutEligibility, retailerId, retailPrice, stacklineSku, title, } = product;
    const img = getEntityImage(stacklineSku, 'product');
    const cleanProductTitle = decodeHTMLCharacters(title || '');
    const purchaseRewardAmount = getPurchaseRewardAmountForProduct(product);
    const rewardPercent = !!retailPrice && !!purchaseRewardAmount ? (purchaseRewardAmount * 100) / retailPrice : 0;
    const matchedProductsRetailerIds = (matchedProducts !== null && matchedProducts !== void 0 ? matchedProducts : []).map((matchedProduct) => matchedProduct.retailerId);
    const retailerIds = _uniq([retailerId, ...matchedProductsRetailerIds]);
    const urlSearchParams = getAppConsistentParams(window.location.search);
    const targetUrl = getProductUrl({ title, stacklineSku, urlSearchParams });
    // Determine mobile view based on viewport width (matching landing-page logic)
    // Landing-page uses theme.breakpoints.values.sm (600px) but CSS uses 500px
    // We'll use 500px to match the CSS breakpoint
    const [isMobileView, setIsMobileView] = useState(() => {
        if (typeof window === 'undefined')
            return false;
        return window.innerWidth < 500;
    });
    useEffect(() => {
        const handleResize = () => {
            setIsMobileView(window.innerWidth < 500);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    // Responsive values matching landing-page
    const baseFontSize = isMobileView ? 11 : 14;
    const retailerLogoWidth = isMobileView ? 15 : 25;
    // Check if product is already in cart using provided function
    const isInCart = isInCartFn ? isInCartFn(stacklineSku) : false;
    // Determine if we should show cart actions
    const canShowCartActions = showCartActions && isServerSideCheckoutEligible(checkoutEligibility) && (onAddToCart || onOpenCart);
    const handleAddToBag = () => __awaiter(void 0, void 0, void 0, function* () {
        if (onAddToCart) {
            try {
                yield onAddToCart(product);
                onOpenCart === null || onOpenCart === void 0 ? void 0 : onOpenCart();
            }
            catch (error) {
                console.error('Error adding to bag:', error);
            }
        }
    });
    // Card styles matching landing-page reference exactly
    const cardStyle = {
        border: '1px solid #dcdddc',
        width: '100%',
        height: '100%',
        borderRadius: 12,
        padding: isMobileView ? 10 : '16px 16px 16px 22px',
        boxSizing: 'border-box',
        transition: 'background-color 0.4s ease-in-out',
    };
    // Title styles matching landing-page reference
    // Uses CSS variable for theming (falls back to foreground color)
    const titleStyle = {
        textDecoration: 'none',
        color: 'var(--color-foreground, #181D56)',
        fontSize: 14,
        fontWeight: 500,
        marginTop: 20,
        width: '100%',
        overflow: 'hidden',
        letterSpacing: 0.36,
        WebkitLineClamp: 3,
        lineHeight: 1.5,
        height: '4.5em',
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        wordBreak: 'break-word',
    };
    // Price container styles matching landing-page reference
    // Uses CSS variable for theming (falls back to foreground color)
    const priceContainerStyle = {
        color: 'var(--color-foreground, #181D56)',
        fontSize: isMobileView ? 16 : 20,
        fontWeight: 600,
        marginTop: 10,
        marginBottom: 10,
        display: 'flex',
        columnGap: 12,
        alignItems: 'center',
    };
    // Image container styles
    const imageContainerStyle = {
        width: 'calc(100% - 24px)',
        maxWidth: 190,
        aspectRatio: '1/1',
        backgroundColor: '#fff',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        margin: '0 auto 0',
    };
    // Hover state handler
    const [isHovered, setIsHovered] = useState(false);
    return (React__default.createElement("div", { style: Object.assign(Object.assign({}, cardStyle), { backgroundColor: isHovered ? '#F6F8FA' : 'transparent' }), onMouseEnter: () => setIsHovered(true), onMouseLeave: () => setIsHovered(false) },
        React__default.createElement("style", null, `
        .concierge-product-card * {
          mix-blend-mode: multiply;
        }
        .concierge-product-card img[alt*="reward-icon"] {
          mix-blend-mode: normal !important;
        }
      `),
        React__default.createElement("div", { className: "concierge-product-card" },
            React__default.createElement(ConciergeRewardPillWithText, { onPillClick: onShareClick, rewardAmount: affiliateRewardAmount !== null && affiliateRewardAmount !== void 0 ? affiliateRewardAmount : 0, rewardCampaignType: CampaignType.AffiliateRewardCampaign, containerStyle: { justifyContent: 'end' }, baseFontSize: baseFontSize }),
            React__default.createElement("a", { href: targetUrl, target: "_blank", rel: "noreferrer", style: imageContainerStyle },
                React__default.createElement(FallbackImage, { src: img, fallbackImage: img.replace('media.brandclub.com', 'media-api.brandclub.com'), alt: title, style: {
                        maxHeight: '100%',
                        maxWidth: '100%',
                        aspectRatio: '1',
                        objectFit: 'contain',
                    } })),
            React__default.createElement("a", { href: targetUrl, target: "_blank", rel: "noreferrer", style: titleStyle }, cleanProductTitle),
            React__default.createElement("div", { style: priceContainerStyle },
                React__default.createElement("div", { style: { display: 'flex', alignItems: 'center' } }, formatMoney(retailPrice)),
                React__default.createElement(ConciergeRetailerIconGroup, { retailerIds: retailerIds, iconSize: retailerLogoWidth, maxSize: 5 })),
            canShowCartActions && (React__default.createElement("div", { style: { marginTop: 8, marginBottom: 8 } }, isInCart ? (React__default.createElement(Button, { variant: "outline", onClick: onOpenCart, className: cn('tw:w-full tw:h-10 tw:text-sm tw:border-[#6b7280] tw:text-[#6b7280]', 'tw:normal-case tw:hover:border-[#4b5563] tw:hover:bg-[#f9fafb]', 'max-[500px]:tw:h-9 max-[500px]:tw:text-xs'), style: {
                    width: '100%',
                    height: isMobileView ? 36 : 40,
                    fontSize: isMobileView ? 12 : 14,
                    borderColor: '#6b7280',
                    color: '#6b7280',
                    textTransform: 'none',
                } },
                React__default.createElement(CheckCircleIcon, { style: { width: 16, height: 16, marginRight: 8 } }),
                "Added to Bag")) : (React__default.createElement(Button, { variant: "default", onClick: handleAddToBag, className: cn('tw:w-full tw:h-10 tw:text-sm tw:text-white tw:normal-case', 'max-[500px]:tw:h-9 max-[500px]:tw:text-xs'), style: {
                    width: '100%',
                    height: isMobileView ? 36 : 40,
                    fontSize: isMobileView ? 12 : 14,
                    backgroundColor: 'var(--color-primary, #1e293b)',
                    color: 'var(--color-primary-foreground, white)',
                    textTransform: 'none',
                } },
                React__default.createElement(ShoppingBag, { style: { width: 16, height: 16, marginRight: 8 } }),
                "Add to Bag")))),
            React__default.createElement(ConciergeRewardPillWithText, { rewardAmount: purchaseRewardAmount !== null && purchaseRewardAmount !== void 0 ? purchaseRewardAmount : 0, rewardCampaignType: hasActiveInstantReward ? CampaignType.DailyDealsRewardCampaign : CampaignType.PurchaseRewardCampaign, rewardPercent: rewardPercent, rewardPercentText: "reward", containerStyle: isMobileView ? { columnGap: 8 } : undefined, baseFontSize: baseFontSize }))));
};

export { ConciergeProductCard };
//# sourceMappingURL=ConciergeProductCard.js.map
